import { useCallback, useMemo } from 'react';
import MojitoPresentation from 'mojito/presentation';
import MojitoServices from 'mojito/services';
import EventListItemDetails from 'modules/event-list/item/details/index.js';
import GenericMarketContent from 'modules/aggregated-market-pane/generic/index.js';
import GameLineMarketContent from 'modules/aggregated-market-pane/game-line/index.js';
import { useMarketItems, useEventItem, useEventsLoadDone } from 'modules/common/hooks';
import withEventCardSkeleton from 'modules/event-cards/skeleton/with-event-card-skeleton.js';

const {
    EventTimingStatus,
    FlexPane,
    Text,
    Button,
    CountryIcon,
    SportIcon,
    SportWatermarkImage,
    SportWatermarkImageTypes,
} = MojitoPresentation.Components;
const Scoreboard = MojitoPresentation.Components.Scoreboard.default;
const { NavigationPathBuilder } = MojitoPresentation.Base.Navigation;
const { IntentActionTrigger } = MojitoPresentation.Base.Intent;
const EventsUtils = MojitoServices.SportsContent.Events.utils;
const { types: MarketGroupTypes, utils: MarketGroupUtils } =
    MojitoServices.SportsContent.MarketGroups;
const VideoStreamsUtils = MojitoServices.VideoStreaming.utils;
const { createAggregatedMarket } = EventsUtils;
const { createGameLineMarketInfo } = MarketGroupUtils;
const { AGGREGATED_MARKET_TYPE } = MarketGroupTypes;

const getHrefLink = (event, appContext) => {
    const getHrefItem = event =>
        NavigationPathBuilder.sportEventPath(event.sportId, event.id, appContext.routeResolver);
    return getHrefItem(event);
};

const useLoadDone = props => useEventsLoadDone([props.eventId]);

const MatchEventCard = withEventCardSkeleton(
    props => {
        const { mojitoTools, eventId, marketId, gameLine, shouldRequestData, elementRef } = props;
        const { config, style, appContext, stringResolver } = mojitoTools;
        const isGameLineCard = !!gameLine;

        const event = useEventItem(eventId, shouldRequestData);
        const onEventClick = useCallback(() => {
            IntentActionTrigger.showSportEvent(event.sportId, event.id);
            appContext.analyticsEmitter.emitAnalytics('eventCardClicked', event.canonicalName);
        }, [event?.sportId, event?.id, event?.canonicalName, appContext.analyticsEmitter]);

        if (!event) {
            return null;
        }

        const isThreeParticipantsEvent = EventsUtils.isThreeParticipantsEvent(event);
        const altText =
            appContext.sportName(event.sportId) || stringResolver.resolveString('$GENERIC.DEFAULT');
        const showBackground =
            config.showBackgroundSportImage &&
            !config.noBackgroundImageSports.includes(event.sportId);
        const isScoreboardEnabled = config.scoreboardEnabledSports.includes(event.sportId);

        return (
            <div style={style.cardWrapper} ref={elementRef}>
                {showBackground && (
                    <BackgroundSportImage
                        {...props}
                        sportId={event.sportId}
                        isGameLine={isGameLineCard}
                    />
                )}
                <FlexPane class="ta-MatchEventCard" config={config.eventCard.container}>
                    <Button
                        class="ta-clickableArea"
                        onClick={onEventClick}
                        config={config.eventCard.clickableArea}
                    >
                        <FlexPane class="ta-headerContainer" config={config.headerContainer}>
                            {config.showSportIcon && (
                                <SportIcon
                                    sportId={event.sportId}
                                    config={config.sportIcon}
                                    alt={altText}
                                />
                            )}
                            {config.showCountryIcon && event.countryCode && (
                                <CountryIcon
                                    countryCode={event.countryCode}
                                    countryName={event.countryName}
                                    config={config.countryIcon}
                                />
                            )}
                            <Text class="ta-headerTitle" config={config.eventCard.title}>
                                {event.typeName}
                            </Text>
                            <EventTimingStatus
                                config={config.headerTime}
                                eventItem={event}
                                hasStreaming={VideoStreamsUtils.isStreamAvailable(event)}
                                hasClockDetails={EventsUtils.hasInPlayClockDetails(event)}
                                inplayTime={EventsUtils.getEventInplayTime(event)}
                            />
                        </FlexPane>
                    </Button>
                    <FlexPane config={config.participantsAndMarketsContainer}>
                        {isGameLineCard ? (
                            <GameLine {...props} event={event} onEventClick={onEventClick} />
                        ) : (
                            <>
                                {isScoreboardEnabled ? (
                                    <Button
                                        class="ta-clickableArea"
                                        onClick={onEventClick}
                                        config={config.eventCard.clickableArea}
                                    >
                                        <Scoreboard
                                            event={event}
                                            config={config.scoreboard}
                                            isOutright={EventsUtils.isOutrightEventType(
                                                event.eventType
                                            )}
                                        />
                                    </Button>
                                ) : (
                                    <EventListItemDetails
                                        hrefLink={getHrefLink(event, appContext)}
                                        config={
                                            isThreeParticipantsEvent
                                                ? style.eventItemDetailsForThreeParticipants
                                                : config.eventItemDetails
                                        }
                                        event={event}
                                        cbClick={onEventClick}
                                    />
                                )}
                                <RegularMarket {...props} event={event} marketId={marketId} />
                            </>
                        )}
                    </FlexPane>
                </FlexPane>
            </div>
        );
    },
    useLoadDone,
    config => config.eventCard.skeletonLoader
);

const BackgroundSportImage = props => {
    const {
        sportId,
        isGameLine,
        mojitoTools: { config },
    } = props;
    const position = isGameLine
        ? SportWatermarkImageTypes.POSITIONS.BOTTOM_LEFT
        : SportWatermarkImageTypes.POSITIONS.TOP_RIGHT;

    return (
        <SportWatermarkImage sportId={sportId} position={position} config={config.sportWatermark} />
    );
};

const GameLine = props => {
    const { mojitoTools, event, gameLine, marketLines, onEventClick } = props;
    const { config, appContext } = mojitoTools;

    const gameLineMarketInfo = createGameLineMarketInfo(marketLines, gameLine);
    const marketIds = gameLineMarketInfo.markets.map(market => market?.id).filter(Boolean);
    const markets = useMarketItems(marketIds);

    return (
        <GameLineMarketContent
            config={config.gameLineMarketContent}
            eventItem={event}
            aggregatedMarket={createAggregatedMarket(gameLineMarketInfo, markets)}
            onEventClick={onEventClick}
            hrefLink={getHrefLink(event, appContext)}
        />
    );
};

const RegularMarket = props => {
    const { mojitoTools, event, marketName, marketId } = props;
    const { config } = mojitoTools;
    const marketIds = useMemo(() => [marketId], [marketId]);
    const markets = useMarketItems(marketIds);
    const infoMarkets = marketIds.map(id => ({ id }));
    const marketInfo = { type: AGGREGATED_MARKET_TYPE.GENERIC, markets: infoMarkets };
    return (
        <>
            <Text class="ta-marketTitle" config={config.marketTitle}>
                {marketName}
            </Text>
            <GenericMarketContent
                config={config.genericMarketContent}
                aggregatedMarket={createAggregatedMarket(marketInfo, markets)}
                eventItem={event}
                showSelectionHeaders={false}
            />
        </>
    );
};

MatchEventCard.getStyle = (config, mode, merge) => {
    return {
        cardWrapper: {
            position: 'relative',
        },
        eventItemDetailsForThreeParticipants: merge(
            config.eventItemDetails,
            config.eventItemDetailsForThreeParticipants
        ),
    };
};

export default MatchEventCard;
